<template>
  <b-card no-body class="kanban-item task-card" @click="$emit('view', task)">
    <!--
      Title, badges, and due date now live on 3 STACKED lines instead of
      one single row — the due date specifically used to sit on the same
      line as the title/badges (far right), which read as floating/
      disconnected from either. Now: line 1 is Title (+ recurring icon) +
      Priority + the 3-dot menu, Priority sitting immediately BEFORE the
      3-dot menu (both pinned to the row's right side); line 2 is Status
      (+ "Due Today", if any) badges, WITH the due date now sharing this
      same line (pinned right after the badges) instead of its own
      separate line below — tightens the card's overall height.

      The 3-dot edit/delete menu (`.item-dropdown`) IS now a normal flex
      sibling of Title/Priority here — `app-kanban.scss`'s app-wide
      `.kanban-item .item-dropdown` rule (`position: absolute; right: 0`,
      `display: none` until `.kanban-item:hover`) is overridden below
      (scoped `::v-deep .item-dropdown`) back to `position: static` +
      always-`display: flex`, so it stays permanently visible, in-flow,
      and pinned to the row's end via `margin-left: auto` — never
      shifting position based on title length, whether Priority is
      editable, or anything else on the card — instead of only appearing
      on hover, floating absolutely, and potentially overlapping content.
    -->
    <div class="d-flex align-items-center card-title-row" style="gap: 4px;">
      <h6
        class="task-title mb-0"
        :class="{ 'task-title--clamp': showStatus }"
      >{{ task.title }}</h6>

      <!-- Recurring-series indicator — shown on BOTH the template (`isRecurring`) and every occurrence it generated (`recurringTaskId` set); see `isPartOfRecurringSeries()` in `task-config.js`. Purely informational, no click behavior of its own. -->
      <feather-icon
        v-if="isRecurring"
        icon="RepeatIcon"
        size="12"
        class="text-muted flex-shrink-0"
        :aria-label="task.isRecurring ? 'Recurring task — repeats automatically' : 'Auto-generated from a recurring task'"
      />

      <!--
        `boundary="window"` alone only controls Popper's FLIP/placement
        math (which side the menu opens on to stay within the window) — it
        does NOT move the menu's actual DOM node, which still renders as a
        normal descendant of this card's ancestors (`.kanban-board-scroll`/
        `--team`, `.kanban-wrapper`, etc. in `TaskKanbanBoard.vue`). Any one
        of those with `overflow` set to anything but `visible` on EITHER
        axis clips this popup's content regardless of where Popper decides
        to place it — including implicitly: per the CSS spec, setting only
        `overflow-x` (or only `overflow-y`) to non-`visible` silently
        upgrades the OTHER axis's computed value to `auto` too, so even an
        ancestor that only meant to clip horizontally still clips
        vertically. That's what caused the "only 2 of 4 priority options
        show, cut off with a stray scrollbar sliver" bug even with
        `boundary="window"` already in place.

        `positionFixed: true` (a real Popper.js v1 option, merged in via
        `popper-opts`) is the actual fix: it makes Popper render this menu
        with `position: fixed` instead of `absolute`. Fixed-position
        elements are positioned relative to the viewport and are NOT
        subject to ancestor `overflow` clipping (only a `transform`/
        `will-change: transform`/similar on an ancestor would change that,
        and none of this card's ancestors set one) — so the menu now
        visually floats above/outside the panel entirely, however deep the
        clipping ancestor chain gets, while `boundary="window"` still keeps
        it flipping to stay on-screen.
      -->
      <b-dropdown
        v-if="canChangePriority"
        size="sm"
        no-caret
        boundary="window"
        :popper-opts="{ positionFixed: true }"
        :variant="`flat-${priorityVariant(task)}`"
        toggle-class="priority-badge-toggle"
        class="flex-shrink-0"
        @click.native.stop
      >
        <template #button-content>
          <b-badge :variant="priorityBadgeVariant(task)" pill class="task-badge">{{ priorityLabel(task) }}</b-badge>
        </template>
        <b-dropdown-item
          v-for="level in priorityLevels"
          :key="level.key"
          @click="$emit('change-priority', { task, priority: level.key })"
        >
          <b-badge :variant="level.variant" pill class="mr-50">&nbsp;</b-badge>{{ level.label }}
        </b-dropdown-item>
      </b-dropdown>
      <b-badge v-else :variant="priorityBadgeVariant(task)" pill class="task-badge flex-shrink-0">{{ priorityLabel(task) }}</b-badge>

      <!-- Same `positionFixed` fix as the Priority dropdown just above, applied here too since this menu sits inside the exact same clipping ancestor chain — see that dropdown's doc comment for the full explanation. `ml-auto` (on the `<b-dropdown>` itself — its root wrapper, not the inner toggle button targeted by `toggle-class`, is the actual flex item on this row) pins it to the row's end so it always sits right after Priority regardless of title/badge width. -->
      <b-dropdown
        variant="link"
        no-caret
        boundary="window"
        :popper-opts="{ positionFixed: true }"
        toggle-class="p-0 item-dropdown"
        class="ml-auto"
        right
        @click.native.stop
      >
        <template #button-content>
          <feather-icon icon="MoreVerticalIcon" size="16" />
        </template>
        <b-dropdown-item v-if="canEdit" @click="$emit('edit', task)">
          <feather-icon icon="EditIcon" size="14" class="mr-50" />Edit
        </b-dropdown-item>
        <b-dropdown-item v-if="canDelete" @click="$emit('delete', task)">
          <feather-icon icon="Trash2Icon" size="14" class="mr-50" />Delete
        </b-dropdown-item>
        <b-dropdown-item
          v-if="!canEdit && !canDelete && !canChangeStatus && !canPing"
          disabled
        >
          <feather-icon icon="LockIcon" size="14" class="mr-50" />View only — creator can edit
        </b-dropdown-item>
        <!--
          Per-TASK ping — distinct from `TaskListView.vue`'s per-EMPLOYEE
          name-bell (which pings about "everything" this person has). Gated
          by `canPingTask` (manager of the assignee — NOT edit-details /
          `createdBy`), same as `TaskListView.vue#canPingFor`. Emits
          `{ empId, empName, task }` — the `task` here is what tells
          `TaskBoard.vue#handlePingEmployee` this is a task-specific ping
          rather than a generic one; see that handler's doc for how the
          two paths diverge from there.
        -->
        <b-dropdown-item v-if="canPing" @click="$emit('ping', pingPayload)">
          <feather-icon icon="BellIcon" size="14" class="mr-50" />Ping for update
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="card-badges-row">
      <div class="card-badges-main">
      <b-badge
        v-if="projectLabel"
        variant="light-primary"
        pill
        class="task-badge task-project-badge flex-shrink-0"
      >
        <feather-icon icon="FolderIcon" size="10" class="mr-25" />{{ projectLabel }}
      </b-badge>
      <!--
        Status has no dedicated column in the per-member team view, so it's
        surfaced as a badge here instead. Priority now lives up on the
        title row (line 1, before the 3-dot menu) instead of here — this
        row is Status (+ "Due Today", if any) only.
      -->
      <b-badge v-if="showStatus" :variant="statusBadgeVariant(task)" pill class="text-nowrap status-badge task-badge">{{ statusLabel(task) }}</b-badge>

      <!--
        "Due Today" is a calm, informational badge — distinct from the
        warning/danger escalation badges below, which only fire once a task
        is actually PAST due (see `isDueToday()`/`getEscalationLevel()` in
        `task-config.js`: the two are mutually exclusive by construction,
        so a card only ever shows one or the other, never both. Uses the
        same `light-*` treatment as every other badge on this card now
        (2026-07-23) — the clock icon + label already read as attention-
        grabbing without a heavy solid fill.
      -->
      <b-badge v-if="isDueTodayFlag" :variant="dueTodayMeta.variant" pill class="text-nowrap task-badge">
        <feather-icon icon="ClockIcon" size="10" class="mr-25" />{{ dueTodayMeta.label }}
      </b-badge>

      <!--
        Due date sits on this same row, right beside the Status/"Due
        Today" badges — but hidden whenever "Due Today" is already shown
        (2026-07-23), since the two said the exact same thing side by side
        ("Due Today" + today's own date, redundant). Still shown for every
        other case (future due date, or an overdue/escalated task) where
        the date itself is the only place that information appears.
      -->
      <small
        v-if="taskDueDate && !isDueTodayFlag"
        class="text-nowrap text-muted"
      >
        <feather-icon icon="CalendarIcon" size="12" class="mr-25" />{{ dueDateDisplay }}
      </small>

      <!--
        Escalation now shares this same Progress/Due-Date row instead of
        sitting on its own oversized line below — sized identically to
        every other badge here (`.task-badge`) rather than standing out.
      -->
      <b-badge
        v-if="escalationLevel"
        pill
        :variant="escalationBadgeMeta.variant"
        :class="`text-nowrap task-badge badge-escalation-${escalationLevel}`"
      >
        {{ escalationBadgeMeta.label }}
      </b-badge>

      <!--
        Recurring occurrences (and any other past-due row without an
        escalation tier) still need a visible overdue signal — mutually
        exclusive with Due Today / escalation badges above.
      -->
      <b-badge
        v-if="showOverdueBadge"
        :variant="overdueMeta.variant"
        pill
        class="text-nowrap task-badge"
      >
        {{ overdueMeta.label }}
      </b-badge>

      <!--
        In-app manager ping indicator (replaces email-only Ping). Soft
        `light-warning` matches other card badges. See `task-ping-store.js` /
        `TaskBoard.vue#handlePingEmployee`.
      -->
      <b-badge
        v-if="isPinged"
        pill
        variant="light-warning"
        class="text-nowrap task-badge task-ping-badge"
      >
        <feather-icon icon="BellIcon" size="10" class="mr-25" />{{ pingBadgeLabel }}
      </b-badge>
      </div>

      <!--
        "Who else is on this batch" — only rendered for a multi-assignee
        task (non-null `groupId`); see `CoAssigneeAvatars.vue`'s own doc
        and `coAssigneesFor()` in `task-config.js`. Rendered outside the
        wrapping badge cluster so avatars stay visible on narrow columns.
      -->
      <co-assignee-avatars v-if="coAssignees.length" :co-assignees="coAssignees" class="card-badges-aside" />
    </div>

    <!--
      Attachment chip — Vuexy Kanban-style paperclip + filename. Opens the
      file when a URL exists; otherwise still labels the card so the detail
      modal can show it. Stop propagation so click doesn't open Edit.
    -->
    <div
      v-if="attachment"
      class="d-flex align-items-center task-attachment-chip mt-50"
      @click.stop
    >
      <feather-icon icon="PaperclipIcon" size="12" class="text-muted mr-25 flex-shrink-0" />
      <a
        v-if="attachment.url"
        :href="attachment.url"
        target="_blank"
        rel="noopener"
        class="text-truncate small"
      >
        {{ attachment.name }}
      </a>
      <span
        v-else
        class="text-truncate small text-muted"
      >
        {{ attachment.name }}
      </span>
    </div>

    <!--
      Description is intentionally NOT shown here — every card in a column
      repeating its own (often near-identical) description text was pure
      clutter. Clicking the card already opens the full task
      in `TaskFormModal.vue` (edit mode — see `TaskBoard.vue#openEditModal`,
      wired to this card's `@click`/`view` event), which shows the full
      Description field, so the detail is still one click away, just not
      duplicated on every card face.

      The assignee avatar is ALSO gone now, in BOTH board modes — it was
      previously kept for "My Team's Tasks" on the reasoning that a card's
      owner varies view-wide, but within any ONE column (this component
      only ever renders inside a single column at a time) every card
      obviously belongs to that column's one member/status already named
      by the column header, so it was just as redundant there.
    -->

  </b-card>
</template>

<script>
import { BCard, BDropdown, BDropdownItem, BBadge } from 'bootstrap-vue'
import moment from 'moment'
import CoAssigneeAvatars from './CoAssigneeAvatars.vue'
import {
  TASK_PRIORITY_LEVELS,
  priorityVariant,
  normalizeTaskPriority,
  statusVariant,
  normalizeTaskStatus,
  getEscalationLevel,
  escalationLevelMeta,
  isDueToday,
  isTaskOverdue,
  TASK_DUE_TODAY_META,
  TASK_OVERDUE_META,
  isPartOfRecurringSeries,
  formatDueDateTime,
  coAssigneesFor,
  taskAssigneeEmpId,
  taskAssigneeName,
  normalizeTaskAttachment,
} from '@/utils/task-config'
import { canPingTask } from '@/utils/task-permissions'
import { getTaskPing, getEmployeePing } from '@/utils/task-ping-store'

export default {
  name: 'TaskCard',
  components: { BCard, BDropdown, BDropdownItem, BBadge, CoAssigneeAvatars },
  props: {
    task: { type: Object, required: true },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    canChangePriority: { type: Boolean, default: false },
    /** Whether the current user may still change status even when details (canEdit/canDelete) are locked. */
    canChangeStatus: { type: Boolean, default: false },
    /** Show a status badge — used in the per-member team view, which has no status columns. */
    showStatus: { type: Boolean, default: false },
    /** Full unscoped task list — used ONLY to look up this task's `groupId` siblings for `coAssignees` below; see `coAssigneesFor()`'s doc in `task-config.js`. */
    allTasks: { type: Array, default: () => [] },
    /** Manager's direct reports — used ONLY to gate the "Ping for update" menu item (`canPing` below) via `canPingTask` (manager of assignee — not `createdBy` / edit-details). Not needed for anything else on this card (edit/delete/priority/status permissions all arrive pre-computed via their own dedicated props). */
    teamMembers: { type: Array, default: () => [] },
    /** Bumped after each Ping so this card re-reads `task-ping-store` (localStorage is not reactive). */
    pingRevision: { type: Number, default: 0 },
  },
  data() {
    return {
      priorityLevels: TASK_PRIORITY_LEVELS,
    }
  },
  computed: {
    projectLabel() {
      return this.task?.projectName ?? this.task?.project_name ?? null
    },
    /** Active per-task ping, or a general per-employee ping targeting this card's assignee. */
    pingMeta() {
      // Depend on `pingRevision` so localStorage-only employee pings re-render.
      // eslint-disable-next-line no-unused-expressions
      this.pingRevision
      return getTaskPing(this.task) || getEmployeePing(taskAssigneeEmpId(this.task))
    },
    isPinged() {
      return !!this.pingMeta
    },
    pingBadgeLabel() {
      const count = Number(this.pingMeta && this.pingMeta.pingCount) || 0
      return count > 1 ? `Pinged ×${count}` : 'Pinged'
    },
    /**
     * Pure, always-live escalation tier — recomputed from the task's
     * CURRENT `dueDate`/`status` on every render (see `getEscalationLevel`
     * doc in `task-config.js`), so editing the due date or marking the
     * task Done clears/changes this badge immediately, with nothing stale
     * cached anywhere on the card itself.
     */
    escalationLevel() {
      return getEscalationLevel(this.task)
    },
    escalationBadgeMeta() {
      return escalationLevelMeta(this.escalationLevel) || { label: '', variant: 'danger' }
    },
    /** Live, uncached — recomputed every render from the task's current dueDate/status, same as `escalationLevel`. */
    isDueTodayFlag() {
      return isDueToday(this.task)
    },
    dueTodayMeta() {
      return TASK_DUE_TODAY_META
    },
    overdueMeta() {
      return TASK_OVERDUE_META
    },
    /** Past deadline with no escalation tier (recurring occurrences skip the ladder). */
    showOverdueBadge() {
      return !this.isDueTodayFlag && !this.escalationLevel && isTaskOverdue(this.task)
    },
    taskDueDate() {
      return this.task?.dueDate || this.task?.due_date || null
    },
    isRecurring() {
      return isPartOfRecurringSeries(this.task)
    },
    /** "22 Jul, 5:00 PM" when a `dueTime` is set, otherwise just "22 Jul" — see `formatDueDateTime()` in `task-config.js`. */
    dueDateDisplay() {
      return formatDueDateTime(this.task, this.formatDate)
    },
    /** "Who else is on this batch" — `[]` for an ordinary, non-multi-assignee task; see `coAssigneesFor()`'s doc in `task-config.js`. */
    coAssignees() {
      return coAssigneesFor(this.task, this.allTasks)
    },
    /** Ping gate for THIS card's task — `canPingTask` (manager of assignee), same rule as `TaskListView.vue#canPingFor`. */
    canPing() {
      return canPingTask(null, this.task, this.teamMembers)
    },
    /** `{ empId, empName, task }` for the `ping` event — the presence of `task` here (vs. `TaskListView.vue`'s per-employee name-bell, which omits it) is what tells `TaskBoard.vue#handlePingEmployee` this is a ping about ONE specific task, not a general "check all your tasks" nudge. */
    pingPayload() {
      return {
        empId: taskAssigneeEmpId(this.task),
        empName: taskAssigneeName(this.task),
        task: this.task,
      }
    },
    /** Normalized attachment for the paperclip chip — `null` when none. */
    attachment() {
      return normalizeTaskAttachment(this.task)
    },
  },
  methods: {
    priorityVariant(task) {
      return priorityVariant(task)
    },
    /**
     * The Priority/Status badges on the card face render the SOFT/pastel
     * `light-*` BootstrapVue badge variant (already used elsewhere in this
     * app — e.g. the column-count badge in `TaskKanbanBoard.vue`,
     * `light-primary`/`light-success` badges in the Invoice module) rather
     * than the bold solid color — a solid red/orange/teal pill for
     * something as routine as "this task's priority is High" read as
     * needlessly loud next to the rest of this now-denser card. The
     * dropdown TOGGLE itself (`flat-${priorityVariant(task)}`, above)
     * keeps its own separate `flat-*` variant — that's the button
     * background, a different concern from this label pill's color.
     */
    priorityBadgeVariant(task) {
      return `light-${priorityVariant(task)}`
    },
    priorityLabel(task) {
      return normalizeTaskPriority(task)
    },
    statusVariant(task) {
      return statusVariant(task)
    },
    /** Soft `light-*` counterpart to `statusVariant()` — see `priorityBadgeVariant()`'s doc comment just above. */
    statusBadgeVariant(task) {
      return `light-${statusVariant(task)}`
    },
    statusLabel(task) {
      return normalizeTaskStatus(task)
    },
    formatDate(value) {
      return value ? moment(value).format('DD MMM') : ''
    },
  },
}
</script>

<style lang="scss" scoped>
// Gives this scoped block access to `$card-border-width`/`$card-border-color`
// below — same import path already used for the same purpose elsewhere in
// this app (e.g. `MyPage.vue`).
@import '~@core/scss/base/bootstrap-extended/include';

// `.kanban-item`/`.card` (see `app-kanban.scss` and
// `@core/scss/base/bootstrap-extended/_card.scss`) were only ever given a
// soft `box-shadow` for definition — `.card { border: none; }` is a
// deliberate, app-wide rule, not something removed this session, and it's
// what made cards read as borderless floating text. That shadow alone is
// too subtle to read reliably (especially against the board's light
// background), so a visible border is added here too, using the exact same
// tokens the codebase's own `.card-bordered` utility already uses
// (`_card.scss`: `border: $card-border-width solid $card-border-color`) —
// consistent with existing convention rather than an invented color.
.task-card {
  border: $card-border-width solid $border-color !important;
  padding: 0.65rem 0.75rem !important;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    border-color: rgba(115, 103, 240, 0.25) !important;
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.08) !important;
  }

  // Title row (line 1) never wraps — only the title itself shrinks
  // (`min-width: 0` + ellipsis below), so it never forces the recurring
  // icon/3-dot menu to wrap or overflow.
  .card-title-row {
    flex-wrap: nowrap;
    min-width: 0;
    max-width: 100%;
    gap: 4px;
  }
  // Badges row (line 2) wraps on narrow columns — primary badges cluster
  // on the first line(s), co-assignee avatars pin to the row end.
  .card-badges-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 0.35rem;
    margin-top: 0.25rem;
    min-width: 0;
    max-width: 100%;
  }
  .card-badges-main {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 0.35rem;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
  }
  .card-badges-aside {
    flex: 0 0 auto;
    margin-left: auto;
    padding-left: 0.15rem;
  }
  .task-title {
    font-weight: 600;
    font-size: 0.95rem;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.35;
    word-break: break-word;

    &--clamp {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      white-space: normal;
    }
  }
  .priority-badge-toggle {
    padding: 0;
    border: none;
    background: transparent;
  }
  // The recurring icon / 3-dot menu (line 1) and every badge (line 2) keep
  // their natural size — only the title (line 1) absorbs overflow via
  // ellipsis, so nothing else is ever squeezed or wrapped mid-badge.
  .card-title-row > *:not(.task-title),
  .card-badges-main > * {
    flex-shrink: 0;
  }

  // Action controls stay fully visible at the card's right edge.
  ::v-deep .item-dropdown,
  .priority-badge-toggle {
    flex-shrink: 0;
    position: relative;
    z-index: 2;
  }

  // Denser badges — the default BootstrapVue pill (`$badge-font-size: 85%`,
  // `$badge-pill-padding-x: 0.5rem`) read as oversized next to this now
  // 3-line, deliberately compact card layout. Applied to Priority/Status/
  // "Due Today" alike so they all read as one consistent, smaller size.
  .task-badge {
    font-size: 0.75rem;
    padding: 0.28em 0.5em;
  }

  .task-attachment-chip {
    min-width: 0;
    a, span {
      max-width: 100%;
    }
  }

  // Priority moved up to the title row (before the 3-dot menu), so it's
  // no longer adjacent to Status here — the old Priority<->Status
  // negative-margin gap tweak that lived on `.status-badge` no longer
  // applies to anything and has been dropped; Status now just takes the
  // row's normal 4px `gap` like every other badge pair.

  // The 3-dot menu (`.item-dropdown`) is app-wide `position: absolute;
  // right: 0` + hover-only `display: none` by default (`app-kanban.scss`,
  // shared by every `.kanban-item`) — which is exactly why it used to feel
  // "floaty"/unstable: invisible until hover, and positioned outside the
  // normal flex flow so it could visually drift relative to Priority/the
  // title rather than sitting a fixed, predictable distance from them.
  // Overridden here to always render, in-flow, as a normal flex sibling
  // of Title/Priority on the title row — restoring `position: static`
  // lets its wrapper (the actual flex item; the toggle button targeted by
  // `::v-deep` here just no longer escapes its own flow) size itself to
  // the button again, and `ml-auto` on that wrapper (set directly in the
  // template, since a plain scoped/`::v-deep` selector can't add margin
  // to a component's own root node from the outside) pins it to the row's
  // end regardless of title length or whether Priority itself is present.
  // `::v-deep` (same escape hatch already used in `TaskListView.vue`'s
  // mobile rules) since the actual `<button class="... item-dropdown">`
  // is rendered by `<b-dropdown>`'s own internals, not directly in this
  // component's template, so a plain scoped selector may not reliably
  // reach it depending on how deep Vue's scoped-attribute stamping goes.
  ::v-deep .item-dropdown {
    display: flex !important;
    align-items: center;
    position: static !important;
    right: auto !important;
  }

  // The 3-dot menu's icon inherited whatever ambient color a plain
  // `variant="link"` button happened to have in scope — reading as a
  // near-invisible pale grey against the row's white background now that
  // it's always visible (no longer gated behind `.kanban-item:hover`, see
  // the always-visible override just above). Forced to a clearly visible,
  // still-muted `$body-color` grey instead (the same tone already used
  // for the due-date text below), darkening further on hover/focus so it
  // unmistakably reads as an interactive control.
  ::v-deep .item-dropdown svg {
    stroke: $body-color !important;
  }
  ::v-deep .item-dropdown:hover svg,
  ::v-deep .item-dropdown:focus svg {
    stroke: $headings-color !important;
  }

  // Escalation badges (see `getEscalationLevel()`/`escalationLevelMeta()` in
  // `task-config.js`): all 3 tiers now use `light-*` variants (2026-07-23 —
  // the previous solid warning/danger/dark fills read as too heavy next to
  // this card's otherwise-sober badges). FINAL still needs to read as the
  // most severe tier though, so instead of a solid fill it gets bold,
  // dark-red (`#5e0000` — same tone as the FINAL escalation email banner in
  // `hr-email-notification-service.js`, for visual consistency) TEXT on
  // its `light-danger` background, rather than white-on-solid-dark.
  .badge-escalation-final {
    color: #5e0000 !important;
    font-weight: 700;
  }
}

// Mobile: cards now render at full phone-viewport width (see the kanban
// board's own responsive rules), which is MORE room than a squeezed
// desktop column ever had, not less. The title row still never wraps
// (there's nothing else on that line to wrap around), but gets a larger
// tap target for the 3-dot menu, matching the same touch-target bump
// applied to the kanban board's own icon buttons.
@media (max-width: 767px) {
  .task-card {
    padding: 0.75rem 0.85rem !important;

    .task-title {
      white-space: normal;
      line-height: 1.35;
    }

    .priority-badge-toggle,
    ::v-deep .item-dropdown {
      min-width: 44px;
      min-height: 44px;
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      padding: 0.5rem !important;
    }
  }
}
</style>
